import { MagicWand } from '@phosphor-icons/react';
import {
  fillPrompt,
  SCENE_FRAME_PROMPT,
  SCENE_MOTION_PROMPT,
  VIDEO_FORMAT_IDS,
  VIDEO_FORMATS,
  VIDEO_MODEL_IDS,
  VIDEO_MODELS,
  VIDEO_QUALITIES,
  VIDEO_QUALITY_IDS,
  type VideoFormat,
  type VideoModel,
  type VideoQuality,
} from '@reelplicate/flows';
import {
  Button,
  Field,
  Inline,
  Modal,
  ModalClose,
  ModalContent,
  SegmentedControl,
  SegmentedControlItem,
  Stack,
  TextArea,
  Well,
} from '@reelplicate/ui';
import { useState } from 'react';

import { timecode } from './scenes';
import type { Scene } from './types';

export type ReplicateRequest = {
  videoModel: VideoModel;
  durationSeconds: 5 | 10;
  format: VideoFormat;
  quality: VideoQuality;
  /** The first frame's redraw prompt, as it will run. */
  framePrompt: string;
  /** How it moves, as it will run. */
  prompt: string;
};

/**
 * Replicate a scene: pick the video model, the length and how it moves. The
 * frame is redrawn with a new person and no on-screen text, then animated.
 * Both prompts show exactly what will run, to edit. `initial` fills it all
 * in (Make again, from what a video was made with).
 */
export function ReplicateSceneModal({
  scene,
  initial,
  pending,
  onOpenChange,
  onReplicate,
}: {
  scene: Scene | null;
  initial?: Partial<ReplicateRequest>;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onReplicate: (scene: Scene, request: ReplicateRequest) => void;
}) {
  return (
    <Modal open={scene !== null} onOpenChange={onOpenChange}>
      {scene && (
        <ReplicateScene
          key={scene.scene}
          scene={scene}
          initial={initial}
          pending={pending}
          onReplicate={onReplicate}
        />
      )}
    </Modal>
  );
}

function ReplicateScene({
  scene,
  initial = {},
  pending,
  onReplicate,
}: {
  scene: Scene;
  initial?: Partial<ReplicateRequest>;
  pending: boolean;
  onReplicate: (scene: Scene, request: ReplicateRequest) => void;
}) {
  const [videoModel, setVideoModel] = useState<VideoModel>(initial.videoModel ?? 'kling');
  const [durationSeconds, setDuration] = useState<5 | 10>(initial.durationSeconds ?? 5);
  const [format, setFormat] = useState<VideoFormat>(initial.format ?? '9:16');
  const [quality, setQuality] = useState<VideoQuality>(initial.quality ?? 'high');
  // 4K is Kling's alone.
  const qualities = VIDEO_QUALITY_IDS.filter(
    (id) => videoModel === 'kling' || !VIDEO_QUALITIES[id].klingOnly,
  );
  // The defaults, filled with this scene: exactly what runs unless edited.
  const defaults = {
    framePrompt: fillPrompt(SCENE_FRAME_PROMPT, { scene: scene.context }),
    prompt: fillPrompt(SCENE_MOTION_PROMPT, { scene: scene.context }),
  };
  const [framePrompt, setFramePrompt] = useState(
    initial.framePrompt ?? defaults.framePrompt,
  );
  const [prompt, setPrompt] = useState(initial.prompt ?? defaults.prompt);
  return (
    <ModalContent
      size="sm"
      title={`Replicate scene ${scene.scene}`}
      description={`${timecode(scene.start)}–${timecode(scene.end)} as an AI video`}
      footer={
        <>
          <ModalClose asChild>
            <Button variant="ghost">Cancel</Button>
          </ModalClose>
          <Button
            leadingIcon={MagicWand}
            loading={pending}
            onClick={() =>
              onReplicate(scene, {
                videoModel,
                durationSeconds,
                format,
                quality,
                framePrompt,
                prompt,
              })
            }
          >
            Replicate
          </Button>
        </>
      }
    >
      <Stack gap={4}>
        <Inline gap={3} alignY="top">
          {scene.frameUrl && (
            <img
              src={scene.frameUrl}
              alt={`Scene ${scene.scene}`}
              className="aspect-[9/16] w-16 shrink-0 rounded-sm object-cover"
            />
          )}
          <Well tone="info">
            A new person and no on-screen text, from this frame. It takes a few minutes
            and lands in your library as a clip.
          </Well>
        </Inline>
        <Field label="Video model" helper={VIDEO_MODELS[videoModel].description}>
          <SegmentedControl
            aria-label="Video model"
            value={videoModel}
            onValueChange={(value) => {
              const model = value as VideoModel;
              setVideoModel(model);
              if (model !== 'kling' && VIDEO_QUALITIES[quality].klingOnly)
                setQuality('high');
            }}
          >
            {VIDEO_MODEL_IDS.map((id) => (
              <SegmentedControlItem key={id} value={id}>
                {VIDEO_MODELS[id].label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Field>
        <Field label="Format" helper="It comes out exactly this shape">
          <SegmentedControl
            aria-label="Format"
            value={format}
            onValueChange={(value) => setFormat(value as VideoFormat)}
          >
            {VIDEO_FORMAT_IDS.map((id) => (
              <SegmentedControlItem key={id} value={id}>
                {VIDEO_FORMATS[id].label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Field>
        <Field
          label="Quality"
          helper={videoModel === 'kling' ? undefined : '4K is Kling only'}
        >
          <SegmentedControl
            aria-label="Quality"
            value={quality}
            onValueChange={(value) => setQuality(value as VideoQuality)}
          >
            {qualities.map((id) => (
              <SegmentedControlItem key={id} value={id}>
                {VIDEO_QUALITIES[id].label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Field>
        <Field label="Length">
          <SegmentedControl
            aria-label="Length"
            value={String(durationSeconds)}
            onValueChange={(value) => setDuration(Number(value) as 5 | 10)}
          >
            <SegmentedControlItem value="5">5 s</SegmentedControlItem>
            <SegmentedControlItem value="10">10 s</SegmentedControlItem>
          </SegmentedControl>
        </Field>
        <PromptField
          label="New first frame"
          helper="Redraws the scene's frame with a new person, before it's animated"
          value={framePrompt}
          fallback={defaults.framePrompt}
          onChange={setFramePrompt}
        />
        <PromptField
          label="How it moves"
          value={prompt}
          fallback={defaults.prompt}
          onChange={setPrompt}
        />
      </Stack>
    </ModalContent>
  );
}

/** A prompt to edit, with a way back to the default. */
function PromptField({
  label,
  helper,
  value,
  fallback,
  onChange,
}: {
  label: string;
  helper?: string;
  value: string;
  fallback: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} helper={helper} count={value.length} maxLength={4000}>
      <Stack gap={1}>
        <TextArea
          value={value}
          aria-invalid={value.length > 4000}
          onChange={(event) => onChange(event.target.value)}
        />
        {value !== fallback && (
          <Button
            size="sm"
            variant="ghost"
            className="self-start"
            onClick={() => onChange(fallback)}
          >
            Reset to default
          </Button>
        )}
      </Stack>
    </Field>
  );
}
